import moment from 'moment'
import React from 'react'
import { makeStyles, Typography } from '@paintscout/ui'
import type { CalendarEvent } from 'paintscout'
import { useProductionCalendar } from '../ProductionCalendarContext'
import { ProjectsByDay } from './ProjectsByDay'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    overflow: 'hidden',
    margin: theme.spacing(2),
    '& > div:last-child > div:last-child': {
      borderBottom: 'none'
    }
  },
  monthDivider: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  monthDividerLabel: {
    fontWeight: 500,
    color: theme.paintscout.palette.brand.Neutral.P60
  }
}))

export default function MobileWeekView(props) {
  const { events, date }: { events: CalendarEvent[]; date: Date } = props
  const { hideWeekends } = useProductionCalendar()

  const classes = useStyles()

  function getStartAndEndOfWeek(date) {
    const startOfWeek = moment(date).startOf('week').toDate()
    const endOfWeek = moment(date).endOf('week').toDate()
    return { startOfWeek, endOfWeek }
  }

  function filterEventsForWeek(events, startOfWeek, endOfWeek) {
    return events.filter((event) => {
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)

      // Check if the event overlaps with the current week
      return eventStart <= endOfWeek && eventEnd >= startOfWeek
    })
  }

  function getDaysOfTheWeek(startOfWeek): Date[] {
    const daysOfTheWeek = []
    for (let i = 0; i < 7; i++) {
      daysOfTheWeek.push(moment(startOfWeek).add(i, 'days').toDate())
    }
    if (hideWeekends) {
      return daysOfTheWeek.filter((day) => {
        const weekday = moment(day).day()
        return weekday !== 0 && weekday !== 6
      })
    }
    return daysOfTheWeek
  }

  function filterEventsForDay(events, day) {
    return events.filter((event) => {
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      return (
        (eventStart <= day && eventEnd >= day) ||
        eventStart.toDateString() === day.toDateString() ||
        eventEnd.toDateString() === day.toDateString()
      )
    })
  }

  const { startOfWeek, endOfWeek } = getStartAndEndOfWeek(date)
  const filteredEvents = filterEventsForWeek(events, startOfWeek, endOfWeek)
  const daysOfTheWeek = getDaysOfTheWeek(startOfWeek)

  return (
    <div className={classes.root}>
      {daysOfTheWeek.map((day, i) => {
        const prev = daysOfTheWeek[i - 1]
        const isMonthChange = prev && moment(day).month() !== moment(prev).month()
        return (
          <React.Fragment key={day.toDateString()}>
            {isMonthChange && (
              <div className={classes.monthDivider}>
                <Typography className={classes.monthDividerLabel}>{moment(day).format('MMMM')}</Typography>
              </div>
            )}
            <ProjectsByDay day={day} events={filterEventsForDay(filteredEvents, day)} />
          </React.Fragment>
        )
      })}
    </div>
  )
}

MobileWeekView.title = (date: Date) => {
  const start = moment(date).clone().startOf('week')
  const end = moment(date).clone().endOf('week')
  if (start.month() === end.month()) {
    return `${start.format('MMMM DD')} – ${end.format('DD')}`
  }
  return `${start.format('MMMM DD')} – ${end.format('MMMM DD')}`
}

MobileWeekView.navigate = (date: Date, action: string) => {
  switch (action) {
    case 'PREV':
      return moment(date).subtract(1, 'week').toDate()
    case 'NEXT':
      return moment(date).add(1, 'week').toDate()
    default:
      return date
  }
}
